import { useEffect, useState } from 'react';
import { X } from 'shared/icons';
import presetManager from '../../services/PresetManager';
import ColumnSelector from './ColumnSelector';

/**
 * PresetModal
 *
 * Modal para criar ou editar presets.
 * Permite definir nome, selecionar colunas e criar colunas customizadas.
 */
const PresetModal = ({ isOpen, mode = 'create', preset, onClose }) => {
    const [name, setName] = useState('');
    const [selectedColumns, setSelectedColumns] = useState([]);
    const [errors, setErrors] = useState({});
    const [isSaving, setIsSaving] = useState(false);

    const handleClose = () => {
        setName('');
        setSelectedColumns([]);
        setErrors({});
        onClose();
    };

    // Carregar dados do preset ao editar
    useEffect(() => {
        if (isOpen && mode === 'edit' && preset) {
            setName(preset.name);
            setSelectedColumns(preset.columns || []);
            setErrors({});
        } else if (isOpen && mode === 'create') {
            setName('');
            setSelectedColumns([]);
            setErrors({});
        }
    }, [isOpen, mode, preset]);

    // Fechar com ESC
    // useEffect(() => {
    //   const handleEsc = (e) => {
    //     if (e.key === 'Escape' && isOpen) {
    //       handleClose();
    //     }
    //   };

    //   if (isOpen) {
    //     document.addEventListener('keydown', handleEsc);
    //   }

    //   return () => {
    //     document.removeEventListener('keydown', handleEsc);
    //   };
    // }, [isOpen, handleClose]);

    const validateForm = () => {
        const newErrors = {};

        if (!name.trim()) {
            newErrors.name = 'Nome do preset e obrigatorio';
        } else if (name.length > 100) {
            newErrors.name = 'Nome muito longo (maximo 100 caracteres)';
        }

        if (selectedColumns.length === 0) {
            newErrors.columns = 'Selecione pelo menos uma coluna';
        }

        // Validar se ha pelo menos uma coluna de Informacao
        const hasInfoColumn = selectedColumns.some(
            (col) => col.type === 'predefined' && col.category === 'identification',
        );

        if (!hasInfoColumn) {
            newErrors.columns =
                'Pelo menos uma coluna de Informacao (Produto ou ID do Inventario) deve estar selecionada.';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSave = async () => {
        if (!validateForm()) {
            return;
        }

        setIsSaving(true);

        try {
            // Reordenar colunas: Informacao primeiro, depois outras
            const infoColumns = selectedColumns.filter(
                (col) => col.type === 'predefined' && col.category === 'identification',
            );
            const otherColumns = selectedColumns.filter(
                (col) => !(col.type === 'predefined' && col.category === 'identification'),
            );
            const orderedColumns = [...infoColumns, ...otherColumns];

            const presetData = {
                name: name.trim(),
                columns: orderedColumns,
            };

            if (mode === 'create') {
                await presetManager.createPreset(presetData);
            } else {
                await presetManager.updatePreset(preset.id, presetData);
            }

            handleClose();
        } catch (error) {
            console.error('Erro ao salvar preset:', error);
            setErrors({ general: error.message });
        } finally {
            setIsSaving(false);
        }
    };

    // const handleAddCustomColumn = (column) => {
    //   setSelectedColumns([...selectedColumns, column]);
    // };

    if (!isOpen) {
        return null;
    }

    return (
        <>
            {/* Overlay */}
            <button
                type="button"
                aria-label="Fechar modal"
                className="fixed inset-0 z-40 bg-black bg-opacity-50"
                onClick={handleClose}
            />

            {/* Modal */}
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                <div className="bg-white dark:bg-mono-950 border border-mono-200 dark:border-mono-800 rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
                    {/* Header */}
                    <div className="flex items-center justify-between px-6 py-4 border-b border-mono-200 dark:border-mono-800">
                        <h2 className="text-xl font-bold text-black dark:text-white">
                            {mode === 'create' ? 'Criar Preset' : 'Editar Preset'}
                        </h2>
                        <button
                            type="button"
                            onClick={handleClose}
                            className="p-2 text-mono-600 dark:text-mono-400 hover:text-black dark:hover:text-white hover:bg-mono-100 dark:hover:bg-mono-900 rounded-lg transition-colors"
                        >
                            <X size={20} weight="bold" />
                        </button>
                    </div>

                    {/* Content */}
                    <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6">
                        {/* Nome do Preset */}
                        <div>
                            <label
                                htmlFor="preset-name-input"
                                className="block text-sm font-semibold text-black dark:text-white mb-2"
                            >
                                Nome do Preset
                            </label>
                            <input
                                id="preset-name-input"
                                type="text"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder="Ex: Meu Preset Personalizado"
                                className={`w-full px-4 py-2 text-sm text-black dark:text-white bg-white dark:bg-mono-950 border ${
                                    errors.name
                                        ? 'border-red-500 dark:border-red-500'
                                        : 'border-mono-200 dark:border-mono-800'
                                } rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400`}
                            />
                            {errors.name && (
                                <p className="mt-1 text-xs text-red-500 dark:text-red-400">{errors.name}</p>
                            )}
                        </div>

                        {/* Botao Criar Coluna Customizada */}
                        {/* <div>
              <button
                onClick={() => onOpenCustomColumn(handleAddCustomColumn)}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-950 border border-purple-200 dark:border-purple-800 rounded-lg hover:bg-purple-100 dark:hover:bg-purple-900 transition-colors"
              >
                <Plus size={18} weight="bold" />
                Criar Coluna Customizada
              </button>
            </div> */}

                        {/* Column Selector */}
                        <ColumnSelector selectedColumns={selectedColumns} onColumnsChange={setSelectedColumns} />

                        {errors.columns && <p className="text-xs text-red-500 dark:text-red-400">{errors.columns}</p>}

                        {/* Erro geral */}
                        {errors.general && (
                            <div className="p-4 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg">
                                <p className="text-sm text-red-500 dark:text-red-400">{errors.general}</p>
                            </div>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-mono-200 dark:border-mono-800">
                        <button
                            type="button"
                            onClick={handleClose}
                            disabled={isSaving}
                            className="px-4 py-2 text-sm font-medium text-mono-700 dark:text-mono-300 hover:text-black dark:hover:text-white hover:bg-mono-100 dark:hover:bg-mono-900 rounded-lg transition-colors disabled:opacity-50"
                        >
                            Cancelar
                        </button>
                        <button
                            type="button"
                            onClick={handleSave}
                            disabled={isSaving}
                            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 dark:bg-blue-500 rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isSaving ? 'Salvando...' : mode === 'create' ? 'Criar Preset' : 'Salvar Alteracoes'}
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
};

export default PresetModal;
